import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useCart } from "../context/CartContext";

const DUMMY_PRODUCTS = [
  { id: 1, name: "Amber & Oud",    price: "799",  category: { name: "Woody" },  description: "Deep resinous warmth with smoky amber base notes.", image: null },
  { id: 2, name: "Rose Petals",    price: "699",  category: { name: "Floral" }, description: "Fresh rose with soft powdery undertones.",          image: null },
  { id: 3, name: "Ocean Breeze",   price: "649",  category: { name: "Fresh" },  description: "Crisp sea air and light citrus for an airy feel.",  image: null },
  { id: 4, name: "Spiced Vanilla", price: "749",  category: { name: "Spice" },  description: "Warm cinnamon, clove, and rich vanilla bean.",      image: null },
];

const PALETTE = {
  Woody:  { bg: "#e8d9c4", accent: "#9b6e3a", emoji: "🪵" },
  Floral: { bg: "#f2e0e8", accent: "#c4607a", emoji: "🌸" },
  Fresh:  { bg: "#d8eee6", accent: "#3a8a68", emoji: "🍃" },
  Spice:  { bg: "#f0e0cc", accent: "#c47a3a", emoji: "🌶️" },
};

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

// Helper — handles both {id, name} object (API) and plain string (dummy)
const getCategoryName = (category) => {
  if (!category) return "";
  if (typeof category === "object") return category.name || "";
  return String(category);
};

export default function FeaturedProducts() {
  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [loginPrompt, setLoginPrompt] = useState(false);
  const navigate      = useNavigate();
  const { addToCart } = useCart();

  const showLogin = () => { setLoginPrompt(true); setTimeout(() => setLoginPrompt(false), 3000); };

  useEffect(() => {
    api.get("products/")
      .then((res) => {
        const data = res.data?.results || res.data || [];
        setProducts(data.length > 0 ? data.slice(0, 4) : DUMMY_PRODUCTS);
      })
      .catch(() => setProducts(DUMMY_PRODUCTS))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="w-full" style={{ background: "#fff", padding: "80px 6%" }}>

      {/* Login toast */}
      {loginPrompt && (
        <div className="fixed top-20 left-1/2 z-50 flex items-center gap-3 rounded-xl px-5 py-3 shadow-xl"
          style={{ transform: "translateX(-50%)", background: "#1a1a1a", color: "#fff", fontFamily: "Georgia, serif", fontSize: "13px", whiteSpace: "nowrap", animation: "toastUp 0.3s ease" }}>
          <span>🔒</span> Please log in first.
          <button onClick={() => navigate("/login")} className="rounded-full px-3 py-1 text-xs font-semibold ml-1"
            style={{ background: "#e07b39", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif" }}>
            Log In
          </button>
        </div>
      )}

      {/* Header */}
      <div className="text-center" style={{ maxWidth: "1200px", margin: "0 auto 48px" }}>
        <h2 className="font-bold" style={{ fontFamily: "Georgia, serif", fontSize: "clamp(22px, 3vw, 32px)", color: "#1a1a1a", marginBottom: "8px" }}>
          Our Best Sellers
        </h2>
        <div style={{ width: "40px", height: "2px", background: "#e07b39", margin: "0 auto 12px" }} />
        <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#888" }}>
          Loved by thousands of candle enthusiasts across India
        </p>
      </div>

      {/* Grid */}
      {loading ? <Skeleton /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6" style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {products.map((p, i) => (
            <MiniCard key={p.id} product={p} index={i} addToCart={addToCart} onLoginRequired={showLogin} />
          ))}
        </div>
      )}

      {/* View all */}
      <div className="text-center mt-12">
        <button onClick={() => navigate("/products")}
          className="rounded-full text-xs font-semibold uppercase transition-all duration-300"
          style={{ padding: "13px 36px", background: "transparent", color: "#1a1a1a", border: "1.5px solid #1a1a1a", cursor: "pointer", fontFamily: "Georgia, serif", letterSpacing: "0.1em" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#1a1a1a"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#1a1a1a"; }}>
          Shop All Products
        </button>
      </div>

      <style>{`@keyframes toastUp { from{opacity:0;transform:translateX(-50%) translateY(10px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }`}</style>
    </section>
  );
}

function MiniCard({ product, index, addToCart, onLoginRequired }) {
  const [hovered, setHovered] = useState(false);
  const [added, setAdded]     = useState(false);
  const isLoggedIn   = !!localStorage.getItem("token");
  const navigate     = useNavigate();

  // Fix: extract category name from object or plain string
  const categoryName = getCategoryName(product.category);
  const palette      = PALETTE[categoryName] || { bg: "#f0ebe3", accent: "#888", emoji: "🕯️" };

  const handleAdd = () => {
    if (!isLoggedIn) { onLoginRequired(); return; }
    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="flex flex-col rounded-2xl overflow-hidden transition-all duration-300"
      style={{ background: "#fff", boxShadow: hovered ? "0 16px 48px rgba(0,0,0,0.11)" : "0 2px 12px rgba(0,0,0,0.05)",
        transform: hovered ? "translateY(-5px)" : "translateY(0)", animation: `cardFade 0.45s ease ${index * 0.08}s both` }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>

      {/* Image / placeholder */}
      <div className="flex items-center justify-center relative overflow-hidden"
        style={{ aspectRatio: "1/1", background: palette.bg, cursor: "default" }}>
        {product.image
          ? <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500" style={{ transform: hovered ? "scale(1.06)" : "scale(1)" }} />
          : <CandleSVG palette={palette} hovered={hovered} />
        }
        {/* Fix: render categoryName string, not product.category object */}
        <div className="absolute top-3 left-3 text-xs rounded-full px-2.5 py-1"
          style={{ background: "rgba(255,255,255,0.88)", color: palette.accent, fontFamily: "Georgia, serif", fontSize: "10px", fontWeight: "600" }}>
          {categoryName || "Candle"}
        </div>
      </div>

      {/* Info */}
      <div className="flex flex-col flex-1 p-4">
        <div className="font-semibold mb-1" style={{ fontFamily: "Georgia, serif", color: "#1a1a1a", fontSize: "14px" }}>
          {product.name}
        </div>
        <div className="font-bold mb-4" style={{ fontFamily: "Georgia, serif", color: "#1a1a1a", fontSize: "16px" }}>
          {inr(parseFloat(product.price || 0))}
        </div>

        <button onClick={() => navigate("/products/" + product.id)}
          className="w-full rounded-full text-xs font-semibold transition-all duration-200"
          style={{ padding: "9px 0", marginBottom: "8px", background: "transparent", color: "#1a1a1a", border: "1.5px solid #1a1a1a", cursor: "pointer",
            fontFamily: "Georgia, serif", letterSpacing: "0.06em" }}>
          View Details
        </button>
        <button onClick={handleAdd}
          className="w-full rounded-full text-xs font-semibold mt-auto transition-all duration-200"
          style={{ padding: "10px 0", background: added ? "#3a8a68" : "#e07b39", color: "#fff", border: "none", cursor: "pointer",
            fontFamily: "Georgia, serif", letterSpacing: "0.06em",
            boxShadow: added ? "none" : hovered ? "0 4px 14px rgba(224,123,57,0.38)" : "none" }}>
          {added ? "Added to Cart ✓" : !isLoggedIn ? "🔒 Log in to Add" : "+ Add to Cart"}
        </button>
      </div>

      <style>{`@keyframes cardFade { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }`}</style>
    </div>
  );
}

function CandleSVG({ palette, hovered }) {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full transition-transform duration-500" style={{ transform: hovered ? "scale(1.04)" : "scale(1)" }}>
      <svg width="72" height="110" viewBox="0 0 72 110" fill="none">
        <ellipse cx="36" cy="18" rx="10" ry="14" fill={palette.accent} opacity="0.18" />
        <path d="M36 6C36 6 42 14 42 20C42 23.3 39.3 26 36 26C32.7 26 30 23.3 30 20C30 14 36 6 36 6Z" fill={palette.accent} opacity="0.9" />
        <path d="M36 15C36 15 38.5 18.5 38.5 21C38.5 22.4 37.4 23.5 36 23.5C34.6 23.5 33.5 22.4 33.5 21C33.5 18.5 36 15 36 15Z" fill="white" opacity="0.7" />
        <line x1="36" y1="26" x2="36" y2="32" stroke="#555" strokeWidth="1.5" strokeLinecap="round" />
        <rect x="16" y="32" width="40" height="5" rx="2.5" fill={palette.accent} opacity="0.5" />
        <rect x="18" y="37" width="36" height="58" rx="6" fill="white" />
        <rect x="18" y="37" width="36" height="58" rx="6" fill={palette.accent} opacity="0.12" />
        <rect x="22" y="55" width="28" height="24" rx="3" fill={palette.accent} opacity="0.18" />
        <rect x="16" y="93" width="40" height="5" rx="2.5" fill={palette.accent} opacity="0.4" />
      </svg>
      <span style={{ fontSize: "13px", marginTop: "4px", opacity: 0.5 }}>{palette.emoji}</span>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6" style={{ maxWidth: "1200px", margin: "0 auto" }}>
      {[...Array(4)].map((_, i) => (
        <div key={i} className="rounded-2xl overflow-hidden" style={{ background: "#fff" }}>
          <div style={{ aspectRatio: "1/1", background: "#f0ebe3", animation: "pulse 1.5s ease infinite" }} />
          <div className="p-4 flex flex-col gap-3">
            <div className="rounded" style={{ height: "14px", background: "#f0ebe3", width: "70%", animation: "pulse 1.5s ease infinite" }} />
            <div className="rounded-full" style={{ height: "32px", background: "#f0ebe3", animation: "pulse 1.5s ease infinite" }} />
          </div>
        </div>
      ))}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
    </div>
  );
}